<template>
  <section class="discography">
    <transition name="move-fade" appear="">
      <h2>Releases</h2>
    </transition>
    <div class="album-list">
      <AlbumCard
        class="album-card load-hidden"
        v-for="album in sortedAlbums"
        :key="album.title"
        :album="album"
        @select="selectedAlbum = album"
      />
    </div>

    <Transition name="fade">
      <AlbumModal
        v-if="selectedAlbum"
        :album="selectedAlbum"
        @close="selectedAlbum = null"
      />
    </Transition>
  </section>
</template>

<script>
import { useHead } from '@vueuse/head';
import AlbumCard from '@/components/discography/AlbumCard.vue';
import AlbumModal from '@/components/discography/AlbumModal.vue';
import ScrollReveal from 'scrollreveal';
import albumsRaw from '@/assets/data/albums.json';

export default {
  name: 'DiscographyView',
  components: {
    AlbumCard,
    AlbumModal,
  },
  mounted() {
    useHead({
      title: 'Discography | NightKill Official',
      meta: [
        {
          name: 'description',
          content:
            'Explore the complete discography of NightKill — melodic heavy metal from Greece. Albums, singles, and epic riffs await.',
        },
        {
          name: 'keywords',
          content:
            'NightKill discography, metal albums, Greek heavy metal, melodic metal releases, metal singles',
        },
        { property: 'og:title', content: 'Discography | NightKill Official' },
        {
          property: 'og:description',
          content:
            'Discover the sonic catalog of NightKill — from crushing guitar tones to melodic vocals. Explore albums & releases.',
        },
        {
          property: 'og:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
        {
          property: 'og:url',
          content: 'https://nightkill.gr/discography',
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: 'NightKill Official' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Discography | NightKill Official' },
        {
          name: 'twitter:description',
          content:
            'Browse NightKill’s discography — melodic heavy metal from Greece. Hear the journey, feel the sound.',
        },
        {
          name: 'twitter:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
      ],
      link: [{ rel: 'canonical', href: 'https://nightkill.gr/discography' }],

      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': albumsRaw.map((album) => ({
              '@type': 'MusicAlbum',
              name: album.title,
              image: `https://nightkill.gr${album.cover}`,
              datePublished: album.year,
              genre: 'Melodic Heavy Metal',
              byArtist: {
                '@type': 'MusicGroup',
                name: 'NightKill',
                url: 'https://nightkill.gr',
              },
              recordLabel: {
                '@type': 'Organization',
                name: album.label,
              },
              track: album.tracks.map((track) => ({
                '@type': 'MusicRecording',
                name: track,
              })),
            })),
          }),
        },
      ],
    });

    this.$nextTick(() => {
      setTimeout(() => {
        const sr = ScrollReveal({
          duration: 800,
          origin: 'bottom',
          distance: '50px',
          easing: 'ease-in-out',
          cleanup: true,
        });

        sr.reveal('.album-card', {
          interval: 180,
        });
      }, 100);
    });
  },
  beforeUnmount() {
    ScrollReveal().destroy();
  },
  data() {
    return {
      selectedAlbum: null,
      albums: albumsRaw,
    };
  },
  computed: {
    sortedAlbums() {
      return [...this.albums].sort((a, b) => b.year - a.year);
    },
  },
};
</script>

<style scoped>
.discography {
  padding: 2rem;
  padding-top: 0;
  text-align: center;
}
.album-list {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: center;
}

h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #2c3e50;
  margin-top: 0;
}

.album-card:hover {
  transform: scale(1.02);
  box-shadow: 0 0 10px #2c3e50;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

/* CSS for the fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Move + Fade Transition */
.move-fade-enter-active,
.move-fade-leave-active {
  transition: all 0.45s ease;
}

.move-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.move-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.move-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.move-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
